import {
  Button,
  Dialog as MuiDialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useFormik } from "formik";

import React, { useState } from "react";

import { Dialog } from "../../../../components/Dialogs/Dialog";

import { upsertPaymentDetailAsync } from "../../../../redux/paymentDetails/paymentDetailsActions";
import { initialPaymentDetails } from "../../../../redux/paymentDetails/defaultState";
import { useDispatch } from "react-redux";

import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { IStoreState } from "../../../../redux/initialStoreState";
import { useBillingCompanyContext } from "../../../../providers";
import { isNotNull } from "../../../../helpers/formatWithCommas";
import { TabsList } from "../../../../components/Tabs/TabsList";
import { TabPanel } from "../../../../components/Tabs/TabPanel";

import { PaymentDetailsData } from "./components/DialogData";
import { useTheme, useMediaQuery } from "@mui/material";

export interface IPaymentDetailsDialogProps {
  open: boolean;
  leadUUID: string;
  /** When set, the payment is saved against the event group (`multiple_events_uuid`) instead of `leads_uuid`. */
  multipleEventsUUID?: string | null;
  onClose: () => void;
  onComplete?: (
    isSuccess: boolean | null,
    cancellationData?: {
      remarks: string;
      cancellation_dead_reason: string;
      leads_uuid: string;
    }
  ) => void;
  isAdvance?: boolean;
  isCancellation?: boolean;
}

export const PaymentDetailsDialog: React.FC<IPaymentDetailsDialogProps> = (
  props
) => {
  const {
    open,
    onClose,
    onComplete,
    leadUUID,
    multipleEventsUUID,
    isAdvance,
    isCancellation,
  } = props;
  const dispatch = useDispatch();
  const companyOutlet = useBillingCompanyContext();

  const [tab, setTab] = useState({
    current: 0,
    previous: 0,
  });
  const [selectedSign, setSelectedSign] = useState<"+" | "-">("+");
  const [openPaymentTypeDialog, setOpenPaymentTypeDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: initialPaymentDetails,

    validate: (values) => {
      const errors: any = {};
      if (!values.amount) {
        errors.amount = "Amount is required";
      }

      if (!values.mode_of_payment) {
        errors.mode_of_payment = "Mode of Payment is required";
      }
      if (!values.remarks) {
        errors.remarks = "Remarks is required";
      }
      if (!values.payment_date) {
        errors.payment_date = "Payment Date is required";
      }

      return errors;
    },
    onSubmit: (values) => {
      const { leads_uuid, ...restValues } = values;
      (dispatch as ThunkDispatch<IStoreState, {}, AnyAction>)(
        upsertPaymentDetailAsync(
          {
            ...restValues,
            ...(multipleEventsUUID
              ? { multiple_events_uuid: multipleEventsUUID }
              : { leads_uuid: leadUUID }),
            company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
          },
          (isSuccess, data) => {
            if (isSuccess && data) {
              if (values.payment_type === "CANCELLATION_ADJUSTMENT") {
                onComplete &&
                  onComplete(true, {
                    remarks: values.remarks,
                    cancellation_dead_reason:
                      values.cancellation_dead_reason || "",
                    leads_uuid: leadUUID,
                  });
              } else {
                onComplete && onComplete(true);
              }
              onClose();
            }
          }
        )
      );
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setTab((prev) => ({
      current: newTab,
      previous: prev.current,
    }));
  };

  React.useEffect(() => {
    const amountStr = values.amount;
    const taxPercentageStr = values.tax_amount_percentage;

    let effectiveAmount = 0;
    let taxAmount = 0;

    if (isNotNull(taxPercentageStr) > 0) {
      taxAmount = (Number(amountStr) * Number(taxPercentageStr)) / 100;
      effectiveAmount = Number(amountStr) + taxAmount;
    } else {
      taxAmount = Number(values.tax_amount);
      effectiveAmount = Number(amountStr) + taxAmount;
    }

    setValues({
      ...values,
      effective_amount: effectiveAmount.toString(),
      ...(isNotNull(taxPercentageStr) > 0 && {
        tax_amount: taxAmount.toString(),
      }),
    });
  }, [
    values.amount,
    values.tax_amount_percentage,
    values.tax_amount,
    setFieldValue,
  ]);

  React.useEffect(() => {
    if (tab.current === 2 || tab.current === 3) {
      setOpenPaymentTypeDialog(true);
    }
  }, [setFieldValue, values.payment_type]);

  React.useEffect(() => {
    if (isCancellation) {
      setTab((prev) => ({
        current: 3,
        previous: prev.current,
      }));
    }
  }, [isCancellation]);

  return (
    <Dialog
      open={open}
      title={"Add Payment Details"}
      onClose={onClose}
      size="md"
      contentWrappedWithForm={{ onSubmit: handleSubmit }}
      actions={[
        { type: "button", label: "Close", variant: "text", onClick: onClose },
        {
          type: "submit",
          label: "Save",

          variant: "contained",
        },
      ]}
    >
      <TabsList
        sx={{
          mb: 2,
          mr: 2,

          ...(isMobile && {
            "& .MuiTabs-flexContainer": {
              flexWrap: "nowrap",
            },

            "& .MuiTab-root": {
              flex: 1,
              minWidth: 0,
              minHeight: 36,
              padding: "4px 2px",
              fontSize: "0.65rem",
              textTransform: "none",
              whiteSpace: "normal",
              lineHeight: 1.1,
            },
          }),
        }}
        varaint="fullWidth"
        selectedTab={tab.current}
        tabs={
          isAdvance && isCancellation
            ? [
              { label: "Transaction" },
              { label: "Final Adjustment" },
              { label: "Postponed Adjustment" },
              { label: "Cancellation Adjustment" },
            ]
            : isAdvance
              ? [{ label: "Transaction" }]
              : [
                { label: "Transaction" },
                { label: "Final Adjustment" },
                { label: "Postponed Adjustment" },
                { label: "Cancellation Adjustment" },
              ]
        }
        onChange={(event, value) => handleTabChange(event, value)}
      />

      <TabPanel value={tab.current} index={0}>
        <PaymentDetailsData
          values={values}
          errors={errors}
          handleChange={handleChange}
          setValues={setValues}
          setFieldValue={setFieldValue}
          payment_type="TRANSACTION"
          isAdvance={isAdvance}
        />
      </TabPanel>
      <TabPanel value={tab.current} index={1}>
        <PaymentDetailsData
          values={values}
          errors={errors}
          handleChange={handleChange}
          setValues={setValues}
          setFieldValue={setFieldValue}
          payment_type="FINAL_ADJUSTMENT"
          isAdvance={isAdvance}
        />
      </TabPanel>

      <TabPanel value={tab.current} index={2}>
        <PaymentDetailsData
          values={values}
          errors={errors}
          handleChange={handleChange}
          setValues={setValues}
          setFieldValue={setFieldValue}
          payment_type="POSTPONED_ADJUSTMENT"
          isAdvance={isAdvance}
        />
      </TabPanel>
      <TabPanel value={tab.current} index={3}>
        <PaymentDetailsData
          values={values}
          errors={errors}
          handleChange={handleChange}
          setValues={setValues}
          setFieldValue={setFieldValue}
          payment_type="CANCELLATION_ADJUSTMENT"
          isAdvance={isAdvance}
        />
      </TabPanel>

      <MuiDialog
        open={openPaymentTypeDialog}
        onClose={() => setOpenPaymentTypeDialog(false)}
      >
        <DialogContent>
          <DialogContentText>{`Are you sure you want to ${tab.current === 2
            ? "perform Postpond Transaction"
            : tab.current === 3
              ? "perform Cancellation Adjustment"
              : ""
            }?`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTab((prev) => ({
                current: prev.previous,
                previous: prev.previous,
              }));
              setOpenPaymentTypeDialog(false);
              onClose();
            }}
            color="primary"
            variant="contained"
          >
            No
          </Button>
          <Button
            onClick={() => {
              setOpenPaymentTypeDialog(false);
            }}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </MuiDialog>
    </Dialog>
  );
};
